// ─────────────────────────────────────────────────────────────────────────────
// Tarea 17, fix round 1 — extrae el paginado de `winnersApi.exportNominationsRaw`, que vivía
// duplicado VERBATIM entre `AdminWinners.tsx` (`exportarNominacionesCrudas`, Task 4.5) y
// `ShineTab.tsx` (`fetchTodasLasNominacionesCrudas`, Tarea 17): mismo LOTE/TOPE, misma condición de
// corte, mismo comentario de justificación. Un único lugar para ambos consumidores.
// ─────────────────────────────────────────────────────────────────────────────
import { winnersApi } from '@/api/recognition';
import type { NominationRawExportRowDto } from '@/api/recognition';

const LOTE_NOMINACIONES_CRUDAS = 200;
const TOPE_NOMINACIONES_CRUDAS = 5000;

/** Resultado del paginado completo: las filas obtenidas y si el recorrido se cortó por el tope. */
export interface NominacionesCrudasPaginadas {
  filas: NominationRawExportRowDto[];
  /**
   * `true` si el recorrido se cortó porque se alcanzó `TOPE_NOMINACIONES_CRUDAS` (5000 filas), NO
   * porque el backend devolvió una página vacía — en ese caso el resultado es INCOMPLETO y el
   * caller debe avisarlo (nunca descargar un Excel truncado en silencio).
   */
  truncado: boolean;
}

/**
 * Pagina `winnersApi.exportNominationsRaw` completo, tope duro de `TOPE_NOMINACIONES_CRUDAS` filas.
 * Corta SOLO por página vacía o por el tope — NUNCA comparando `filas.length` contra `res.total`:
 * `total` cuenta NOMINACIONES, no filas (`NominationsRawExportPageDto`, doc comment en `types.ts`),
 * y una nominación grupal aporta una fila por CANDIDATO, así que `filas.length` puede alcanzar o
 * superar `total` mucho antes de haber pedido todas las páginas.
 *
 * NO atrapa errores de red/HTTP: el caller decide cómo mostrarlos (ambos consumidores actuales,
 * `AdminWinners.tsx` y `ShineTab.tsx`, envuelven la llamada en su propio try/catch con su propio
 * flag de error y su propio flag de "exportando" para deshabilitar el botón mientras corre).
 */
export async function fetchNominacionesCrudasPaginado(
  campaignId: string,
): Promise<NominacionesCrudasPaginadas> {
  const filas: NominationRawExportRowDto[] = [];
  let pagina = 1;
  do {
    const res = await winnersApi.exportNominationsRaw(campaignId, pagina, LOTE_NOMINACIONES_CRUDAS);
    if (res.filas.length === 0) break;
    filas.push(...res.filas);
    pagina += 1;
  } while (filas.length < TOPE_NOMINACIONES_CRUDAS);
  return { filas, truncado: filas.length >= TOPE_NOMINACIONES_CRUDAS };
}
